import React, { FC, useState } from 'react';
import { Button, Card, DateRangePicker } from '@features/ui';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DateValueType } from '@lib/utils.ts';
import { TReportNewUserByMonth, TReportNewUserByMonthParams } from '@src/types';

interface NewUsersData extends TReportNewUserByMonth {
  values?: string;
}

type NewUsersPayload = {
  value: number;
  values?: string;
  payload?: NewUsersData;
  color?: string;
  dataKey?: string;
};

interface TooltipProps {
  active?: boolean;
  payload?: NewUsersPayload[];
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #B6D72C14',
        }}
      >
        <p className={'text-[#B6D72C] text-xl'}>{payload[0]?.payload?.count}</p>
        <p className={'text-[#999999] text-[10px]'}>
          {payload[0]?.payload?.date}
        </p>
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  payload?: NewUsersPayload[];
}

const CustomLegend: React.FC<CustomLegendProps> = ({ payload }) => {
  if (!payload) return null;

  return (
    <div className={'flex justify-between items-center mb-5'}>
      <p className={'text-2xl'}>New Users By Months</p>
      <div className={'flex gap-x-6 p-2.5'}>
        {payload.map((entry, index) => (
          <div key={index} className={'flex items-center gap-x-1.5'}>
            <div
              className={`w-9 h-3`}
              style={{
                backgroundColor: '#B6D72C',
              }}
            />
            <p>{entry?.payload?.values}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface ReportChartProps {
  data?: NewUsersData[];
  refetchData: (params: TReportNewUserByMonthParams) => Promise<void>;
}
export const ReportChart: FC<ReportChartProps> = ({ data, refetchData }) => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);

  const handleGetData = async () => {
    await refetchData({
      start_date: valueStart?.startDate?.toISOString() ?? null,
      end_date: valueEnd?.startDate?.toISOString() ?? null,
    });
  };

  return (
    <Card className={'mt-4.5 pb-10'}>
      <div
        className={
          'flex lg:flex-row flex-col justify-between items-start mb-5 gap-6'
        }
      >
        <div className={'w-full flex gap-6 mb-5'}>
          <DateRangePicker
            asSingle={true}
            className={'max-w-[239px]'}
            placeholder={'From Date'}
            size={'sm'}
            value={valueStart}
            variant={'outline'}
            onChange={setValueStart}
          />
          <DateRangePicker
            asSingle={true}
            className={'max-w-[239px]'}
            placeholder={'To Date'}
            size={'sm'}
            value={valueEnd}
            variant={'outline'}
            onChange={setValueEnd}
          />
        </div>
        <div className={'flex gap-5'}>
          <Button size={'sm'} variant={'lime'} onClick={handleGetData}>
            Get
          </Button>
        </div>
      </div>
      <ResponsiveContainer height={770} width="100%">
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 30,
          }}
        >
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'transparent' }}
          />
          <Legend
            align="center"
            content={<CustomLegend />}
            layout="horizontal"
            verticalAlign="top"
          />
          <CartesianGrid stroke="#919EAB3D" vertical={false} />
          <XAxis
            angle={-40}
            dataKey="date"
            dy={10}
            fontSize={12}
            padding={{ left: 0, right: 0 }}
            stroke="#919EAB"
            textAnchor="end"
            tickLine={false}
          />
          <YAxis
            fontSize={12}
            stroke="#919EAB"
            tickLine={false}
            yAxisId="left"
          />
          <Bar
            barSize={19}
            dataKey="count"
            fill="#B6D72C"
            values={'New Users'}
            yAxisId="left"
          />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};
